import type { PersonaContent } from '../schemas/persona.js';

/**
 * The persona as the interviewer receives it (§10.3): one block of French
 * prose, rendered from ONE version's content.
 *
 * PURE and TOTAL - no base, no clock, no scale, no tone library. That is the
 * whole point: the block is rendered from the COPY FROZEN IN THE VERSION, never
 * from the current involvement scale or the current tone library. A session
 * that is running keeps the wording it started with, even while somebody
 * rewrites a cran in Réglages (§4, §11.5).
 *
 * ## The order is fixed, and §10.3 argues it
 *
 * Direction, histoire, présentation, implication, ton. The story comes early
 * because the involvement points back at it; the presentation comes before the
 * involvement because it says WHO is speaking before saying how much they give
 * away. The persona editor lays its fields out in the same order, deliberately:
 * two orders for the same thing cost more than one long screen (mockup README).
 *
 * ## What is NOT here
 *
 * **Nothing about « ne pas être une IA ».** That rule left the persona (§10.1):
 * it holds for every persona and every session, so it arrives once, through the
 * global rules of §11.3, and lot 4 is what serves it. A copy here would be the
 * same sentence in every persona, which is exactly what moving it out prevents.
 *
 * An EMPTY field is omitted rather than rendered by a hollow mention: « Ton
 * histoire : (aucune) » teaches the model that the persona has no story, which
 * is not the same instruction as saying nothing about it.
 *
 * The labels are fixed and French (§10.3), spelled with straight apostrophes:
 * this is text a model reads, not screen chrome.
 */
/**
 * The labels of §10.3 open a sentence the operator's text CLOSES, so the block
 * ends on a full stop - unless the text already ends on one.
 *
 * The rule exists because both spellings are in the base: the seeded crans end
 * on a period, a self-introduction direction (« dis en une phrase que tu
 * travailles sur les récits de métier ») usually does not, and appending
 * blindly produced « ... dans ton histoire.. » on the very first fixture.
 * Nothing an operator wrote is removed; a closing mark is only ADDED where one
 * is missing.
 */
function closed(text: string): string {
  return /[.!?…:»]$/.test(text) ? text : `${text}.`;
}

export function renderPersonaDirective(content: PersonaContent): string {
  const blocks: string[] = [content.direction];

  if (content.background !== '') {
    blocks.push(
      `Ton histoire, dans laquelle tu peux puiser selon ton degré d'implication : ${closed(content.background)}`,
    );
  }

  blocks.push(
    content.presentation.kind === 'named'
      ? `Tu t'appelles ${content.presentation.firstName}. Pour te présenter : ${closed(content.presentation.direction)}`
      : "Tu n'as pas de nom et tu ne parles jamais de toi.",
  );

  blocks.push(
    `Degré d'implication, ${content.involvement.name} : ${closed(content.involvement.direction)}`,
  );

  if (content.tone !== null) {
    blocks.push(`Ton, ${content.tone.name} : ${closed(content.tone.text)}`);
  }

  /*
    A BLANK LINE between blocks, and not a single newline: each block is one
    instruction, and prompts that run them together get read as one paragraph
    whose second sentence qualifies the first. Nothing wraps the whole thing -
    the persona is the first of several blocks the interviewer receives (§10.3),
    and it is the assembler of lot 4 that separates them.
  */
  return blocks.join('\n\n');
}
